import React, { useCallback, useEffect, useState } from "react";
import { ReportSummaryItemApi } from "../api/Reports";
import Button, { ButtonColorTypes } from "../shared-components/Button";
import { PlusSmIcon } from "../shared-components/icons";
import ModalAffiliationAdd from "./ModalAffiliationAdd";
import { useReportDetail } from "../reports/ReportDetailProvider";
import { useFetchItemsForReview } from "../api/ItemsApi";
import { ItemTypes } from "../api/Items";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import AffiliationItem from "./AffiliationItem";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import PaginationInMemory from "../shared-components/PaginationInMemory";
import { useApplicationToast } from "../webcontent/ApplicationToastProvider";
import { CheckBadgeIcon, TrashIcon } from "@heroicons/react/24/outline";
import { classNames } from "../utils";

type AffilitionListProps = {
  report: ReportSummaryItemApi;
  isReadOnly: boolean;
  handleExit: () => void;
};

const PAGE_SIZE = 10;

const AffilitionList = (props: AffilitionListProps) => {
  const { showLargeToast } = useApplicationToast();
  const { blueprint, isLoadingBlueprint, errorBlueprint } = useReportDetail();
  const [page, setPage] = useState(1);
  const {
    isLoading: affiliationsLoading,
    data: affiliationItems,
    mutate: getAffiliations,
    isSuccess: affiliationSuccess,
    error: errorAffiliations,
  } = useFetchItemsForReview();

  const loadAffiliations = useCallback(() => {
    if (props.report.report_uuid) {
      getAffiliations({
        report_uuid: props.report.report_uuid,
        item_type_name: [ItemTypes.AFFILIATION],
        page_num: page,
        page_size: PAGE_SIZE,
      });
    }
  }, [props.report.report_uuid, getAffiliations, page]);

  useEffect(() => {
    loadAffiliations();
  }, [loadAffiliations]);

  const [showModal, setShowModal] = useState(false);
  return (
    <div>
      <div className="flex justify-between items-center py-2">
        <div>Affiliation</div>
        <div>
          <Button
            text={
              <div className="flex items-center gap-2 text-gray-500">
                <PlusSmIcon className="w-5" />
                Add Affiliation
              </div>
            }
            onClick={() => {
              setShowModal(true);
            }}
            disabled={props.isReadOnly}
            colorType={ButtonColorTypes.custom}
            className="border"
          />
        </div>
      </div>
      {(errorBlueprint || errorAffiliations) && (
        <ErrorMessageContainer
          message={"Could not load affiliation information"}
        />
      )}
      <div>
        {(affiliationsLoading || isLoadingBlueprint) && (
          <>
            <LoadingSpinner />
          </>
        )}
        {affiliationSuccess && (
          <div>
            {affiliationItems.items.map((item) => (
              <div key={item.item.external_id}>
                <AffiliationItem
                  item={item}
                  report={props.report}
                  handleAction={(action, values) => {
                    if (action === "remove" || action === "edit") {
                      loadAffiliations();
                      showLargeToast?.({
                        title:
                          action === "remove"
                            ? "Affiliation Removed!"
                            : "Affiliation Edited!",
                        message: `Affiliation ${
                          action === "remove" ? "removed" : "edited"
                        }`,
                        icon: (
                          <div
                            className={classNames(
                              "rounded-full p-2",
                              action === "remove"
                                ? "bg-red-100 text-red-600"
                                : "bg-green-100 text-green-600"
                            )}
                          >
                            {action === "remove" ? (
                              <TrashIcon className="w-6" />
                            ) : (
                              <CheckBadgeIcon className="w-6" />
                            )}
                          </div>
                        ),
                      });
                    }
                  }}
                  behaviors={blueprint?.behaviors}
                  isReadOnly={props.isReadOnly}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <PaginationInMemory
          totalCount={affiliationItems?.total || 0}
          pageSize={PAGE_SIZE}
          currentPage={page}
          pageChange={(page: number) => {
            setPage(page);
            loadAffiliations();
          }}
        />
      </div>

      {props.report.report_uuid && blueprint && (
        <ModalAffiliationAdd
          showModal={showModal}
          close={(item) => {
            setShowModal(false);
            if (item) {
              loadAffiliations();
              showLargeToast?.({
                title: "Success!",
                message: "Affiliation successfully added",
                icon: (
                  <div className="bg-green-100 text-green-600 rounded-full p-2">
                    <CheckBadgeIcon className="w-6" />
                  </div>
                ),
              });
            }
          }}
          initialState={{ report: props.report }}
          behaviors={blueprint.behaviors}
        />
      )}
    </div>
  );
};

export default AffilitionList;
